import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import imageCompression from 'browser-image-compression';
import { 
  Car, 
  Upload, 
  FileText, 
  CheckCircle, 
  X, 
  ArrowRight,
  Loader2,
  Info,
  Image as ImageIcon,
  AlertTriangle
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { id: "cession", name: "Cession ou facture d'achat", required: true, multiple: true },
  { id: "carte_grise_etrangere", name: "Carte(s) grise(s) étrangère(s)", required: true, hint: "Teil I et Teil II pour l'Allemagne, plusieurs documents si nécessaire", multiple: true },
  { id: "quitus_fiscal", name: "Quitus fiscal", required: true, multiple: true },
  { id: "controle_technique", name: "Contrôle technique (-6 mois)", required: false, hint: "Si véhicule +4 ans", multiple: true },
  { id: "assurance", name: "Attestation d'assurance", required: true, multiple: true },
  { id: "justificatif_domicile", name: "Justificatif de domicile (-6 mois)", required: true, hint: "Facture électricité, téléphone...", multiple: true },
  { id: "permis_conduire", name: "Permis de conduire", required: true, hint: "Recto et verso", multiple: true },
  { id: "piece_identite", name: "Pièce d'identité", required: true, hint: "Recto et verso", multiple: true },
  { id: "certificat_conformite", name: "Certificat de conformité européen (COC)", required: false, hint: "Si TVV ou n° réception européen incomplet", special: true, multiple: true }
];

const DocumentsPage = () => {
  const navigate = useNavigate();
  const { dossierId } = useParams();
  // Documents is now an object where each key contains an array of documents
  const [documents, setDocuments] = useState({});
  const [uploading, setUploading] = useState({});
  const [dossier, setDossier] = useState(null);

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        const response = await axios.get(`${API}/dossiers/${dossierId}`);
        setDossier(response.data);
        if (response.data.documents) {
          // Convert old format (single document) to new format (array of documents)
          const convertedDocs = {};
          Object.entries(response.data.documents).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              convertedDocs[key] = value;
            } else if (value) {
              convertedDocs[key] = [value];
            }
          });
          setDocuments(convertedDocs);
        }
      } catch (error) {
        toast.error("Dossier non trouvé");
        navigate('/');
      }
    };
    fetchDossier();
  }, [dossierId, navigate]);

  const processAndUpload = async (file, docType) => {
    setUploading(prev => ({ ...prev, [docType]: true }));
    
    try {
      // Compress image to under 900KB
      const options = {
        maxSizeMB: 0.9,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };
      
      const compressedFile = await imageCompression(file, options);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result;
        
        try {
          // Get existing documents for this type
          const existingDocs = documents[docType] || [];
          const newDocs = [...existingDocs, base64Data];
          
          await axios.post(`${API}/dossiers/${dossierId}/documents`, {
            document_type: docType,
            data: JSON.stringify(newDocs), // Send array of documents
            filename: `${docType}_${newDocs.length}.jpg`,
            is_array: true
          });
          
          setDocuments(prev => ({ ...prev, [docType]: newDocs }));
          toast.success("Document ajouté avec succès");
        } catch (error) {
          toast.error("Erreur lors de l'upload");
          console.error(error);
        }
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      toast.error("Erreur lors de la compression");
      console.error(error);
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  const handleFileSelect = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error("Format non supporté. Utilisez une image ou un PDF.");
      return;
    }
    
    if (file.type.startsWith('image/')) {
      await processAndUpload(file, docType);
    } else {
      // Handle PDF
      setUploading(prev => ({ ...prev, [docType]: true }));
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const existingDocs = documents[docType] || [];
          const newDocs = [...existingDocs, reader.result];
          
          await axios.post(`${API}/dossiers/${dossierId}/documents`, {
            document_type: docType,
            data: JSON.stringify(newDocs),
            filename: file.name,
            is_array: true
          });
          setDocuments(prev => ({ ...prev, [docType]: newDocs }));
          toast.success("Document ajouté avec succès");
        } catch (error) {
          toast.error("Erreur lors de l'upload");
        } finally {
          setUploading(prev => ({ ...prev, [docType]: false }));
        }
      };
      reader.readAsDataURL(file);
    }
    // Reset input
    e.target.value = '';
  };

  const removeDocument = (docType, index) => {
    setDocuments(prev => {
      const currentDocs = prev[docType] || [];
      if (index === undefined) {
        // Remove all documents for this type
        const newDocs = { ...prev };
        delete newDocs[docType];
        return newDocs;
      } else {
        // Remove specific document by index
        const newDocsArray = currentDocs.filter((_, i) => i !== index);
        if (newDocsArray.length === 0) {
          const newDocs = { ...prev };
          delete newDocs[docType];
          return newDocs;
        }
        return { ...prev, [docType]: newDocsArray };
      }
    });
    
    // Update backend
    const updateBackend = async () => {
      try {
        const currentDocs = documents[docType] || [];
        const newDocsArray = index !== undefined 
          ? currentDocs.filter((_, i) => i !== index)
          : [];
        
        await axios.post(`${API}/dossiers/${dossierId}/documents`, {
          document_type: docType,
          data: JSON.stringify(newDocsArray),
          filename: `${docType}.jpg`,
          is_array: true
        });
      } catch (error) {
        console.error("Error updating backend:", error);
      }
    };
    updateBackend();
  };

  const getProgress = () => {
    const required = DOCUMENT_TYPES.filter(d => d.required);
    const uploaded = required.filter(d => documents[d.id] && documents[d.id].length > 0);
    return (uploaded.length / required.length) * 100;
  };

  const canContinue = () => {
    return DOCUMENT_TYPES.filter(d => d.required).every(d => documents[d.id] && documents[d.id].length > 0);
  };

  const handleContinue = () => {
    if (!canContinue()) {
      toast.error("Veuillez ajouter tous les documents requis");
      return;
    }
    navigate(`/recapitulatif/${dossierId}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#000091] rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-[#000091]">Carte Grise Étrangère</h1>
              <p className="text-xs text-slate-500">Documents justificatifs</p>
            </div>
          </div>
        </div>
        <div className="h-1 flex">
          <div className="flex-1 bg-[#002395]"></div>
          <div className="flex-1 bg-white"></div>
          <div className="flex-1 bg-[#ED2939]"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              Documents fournis
            </span>
            <span className="text-sm text-slate-500">
              {Object.keys(documents).length} / {DOCUMENT_TYPES.length}
            </span>
          </div>
          <Progress value={getProgress()} className="h-2" />
        </div>

        {dossier && (
          <div className="bg-[#000091]/5 border border-[#000091]/20 rounded-lg p-4 mb-8">
            <p className="text-sm text-[#000091]">
              <span className="font-semibold">Dossier:</span>{' '}
              {dossier.client.prenom} {dossier.client.nom} - {dossier.vehicle.marque} {dossier.vehicle.modele}
            </p>
          </div>
        )}

        {/* Instructions */}
        <Card className="border-slate-200 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg text-[#0F172A]">
              <Upload className="w-5 h-5 text-[#000091]" />
              Importer vos documents
            </CardTitle>
            <CardDescription>
              Importez vos documents depuis votre appareil ou prenez-les en photo. 
              Les images sont automatiquement compressées (max 1 Mo).
            </CardDescription>
          </CardHeader>
        </Card>

        {/* EU Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 font-medium">Véhicules Union Européenne uniquement</p>
            <p className="text-xs text-blue-700 mt-1">
              Ces documents sont requis pour tous les véhicules importés de l'Union Européenne (Allemagne, Belgique, Italie, Espagne, etc.). 
              Pour les véhicules hors UE, contactez un de nos experts.
            </p>
          </div>
        </div>

        {/* Document list */}
        <div className="space-y-4" data-testid="documents-list">
          {DOCUMENT_TYPES.map((docType) => {
            const docList = documents[docType.id] || [];
            const hasDocuments = docList.length > 0;
            
            return (
            <Card 
              key={docType.id} 
              className={`border-slate-200 ${hasDocuments ? 'border-green-300 bg-green-50/50' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${hasDocuments ? 'bg-green-100' : 'bg-slate-100'}`}>
                      {hasDocuments ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <FileText className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-[#0F172A]">{docType.name}</h3>
                        {docType.required && (
                          <span className="text-xs text-red-500">*</span>
                        )}
                      </div>
                      {docType.hint && (
                        <p className="text-xs text-slate-500 mt-0.5">{docType.hint}</p>
                      )}
                      
                      {/* Display uploaded documents */}
                      {hasDocuments && (
                        <div className="mt-3 space-y-2">
                          {docList.map((doc, index) => (
                            <div 
                              key={index} 
                              className="flex items-center gap-2 bg-white rounded-lg p-2 border border-green-200"
                            >
                              <ImageIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                              <span className="text-xs text-green-700 flex-1">
                                Document {index + 1}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeDocument(docType.id, index)}
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                data-testid={`remove-${docType.id}-${index}-btn`}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                          <p className="text-xs text-green-600 font-medium">
                            {docList.length} document{docList.length > 1 ? 's' : ''} ajouté{docList.length > 1 ? 's' : ''}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {uploading[docType.id] ? (
                      <Loader2 className="w-5 h-5 text-[#000091] animate-spin" />
                    ) : (
                      <label>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-[#000091] text-[#000091] cursor-pointer"
                          asChild
                        >
                          <span>
                            <Upload className="w-4 h-4 mr-1" />
                            {hasDocuments ? 'Ajouter' : 'Importer'}
                          </span>
                        </Button>
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={(e) => handleFileSelect(e, docType.id)}
                          data-testid={`upload-${docType.id}-input`}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )})}
        </div>

        {/* Warning */}
        {!canContinue() && (
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-800 font-medium">Documents manquants</p>
              <p className="text-xs text-yellow-700 mt-1">
                Veuillez ajouter tous les documents obligatoires (*) pour continuer.
              </p>
            </div>
          </div>
        )}

        {/* Continue button */}
        <div className="mt-8 flex justify-between">
          <Button
            variant="outline"
            onClick={() => navigate(`/signature/${dossierId}`)}
            className="border-slate-300"
            data-testid="back-to-signature-btn"
          >
            ← Retour à la signature
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!canContinue()}
            className="bg-[#000091] hover:bg-[#000091]/90"
            data-testid="continue-to-recap-btn"
          >
            Continuer vers le récapitulatif
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default DocumentsPage;
